import { Chart, registerables } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Panel } from '../../../../components/panels/Panel';
import { formatVACAeroDashboardDuration, formatVACAeroDashboardPercentage } from '../../../../services/dashboard/vacaero-dashboard-service';
import { VACAeroDashboardNoData } from './VACAeroDashboardBlocks';

Chart.register(...registerables);

const MASTERY_COLOR = '#4164FD';
const MASTERY_BACKGROUND_COLOR = 'rgba(65, 100, 253, 0.16)';
const CYCLE_TIME_COLOR = 'rgb(255, 99, 132)';
const CYCLE_TIME_BACKGROUND_COLOR = 'rgba(255, 99, 132, 0.16)';
const COMPLIANCE_COLOR = 'rgb(153, 102, 255)';
const COMPLIANCE_BACKGROUND_COLOR = 'rgba(153, 102, 255, 0.16)';
const ALERT_COLOR = '#4164FD';
const BAD_TOOL_USAGE_COLOR = 'rgb(255, 159, 64)';
const REFERENCE_COLOR = 'rgba(255, 255, 255, 0.45)';

const getCommonScale = () => {
  return {
    border: { display: false },
    ticks: { color: '#777777' },
    grid: { color: '#333333' },
  };
};

const getCommonLineDataset = (color, backgroundColor) => {
  return {
    borderColor: color,
    backgroundColor,
    pointBackgroundColor: color,
    pointBorderColor: color,
    pointRadius: 2,
    pointHoverRadius: 4,
    borderWidth: 2,
    tension: 0.3,
    spanGaps: true,
    fill: true,
  };
};

const getDynamicScoreMin = (values) => {
  const validValues = values.filter(Number.isFinite);

  if (validValues.length === 0) return 0;

  const minValue = Math.min(...validValues);

  return Math.max(0, Math.floor(minValue * 0.95));
};

export const VACAeroDashboardMasteryEvolutionChart = ({ evolution }) => {
  const masteryValues = evolution.map(({ masteryIndex }) => masteryIndex);
  const hasData = masteryValues.some(Number.isFinite);
  const minValue = getDynamicScoreMin(masteryValues);

  const data = {
    labels: evolution.map(({ label }) => label),
    datasets: [
      {
        ...getCommonLineDataset(MASTERY_COLOR, MASTERY_BACKGROUND_COLOR),
        label: 'Indice de maîtrise du geste',
        data: masteryValues,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length === 0) return '';
            return evolution[items[0].dataIndex]?.fullLabel ?? '';
          },
          label: (context) => `Indice : ${Math.round(context.parsed.y)} / 100`,
        },
      },
    },
    scales: {
      x: {
        ...getCommonScale(),
        ticks: { color: '#777777', autoSkip: true, maxRotation: 0 },
      },
      y: {
        ...getCommonScale(),
        min: minValue,
        max: 100,
      },
    },
  };

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} height="h-[340px]">
      <div className="mb-4 shrink-0 text-sm font-semibold uppercase text-perception-gray-400">
        Évolution de l'indice de maîtrise du geste
      </div>

      <div className="min-h-0 flex-1">
        {hasData ? <Line data={data} options={options} /> : <VACAeroDashboardNoData />}
      </div>
    </Panel>
  );
};

export const VACAeroDashboardCycleTimeEvolutionChart = ({ evolution, theoreticalCycleTime }) => {
  const hasData = evolution.some(({ averageCycleTime: value }) => Number.isFinite(value));

  const datasets = [
    {
      ...getCommonLineDataset(CYCLE_TIME_COLOR, CYCLE_TIME_BACKGROUND_COLOR),
      label: 'Temps de cycle moyen',
      data: evolution.map(({ averageCycleTime: value }) => value),
    },
  ];

  if (Number.isFinite(theoreticalCycleTime)) {
    datasets.push({
      label: 'Temps de cycle théorique',
      data: evolution.map(() => theoreticalCycleTime),
      borderColor: REFERENCE_COLOR,
      backgroundColor: 'transparent',
      pointRadius: 0,
      pointHoverRadius: 0,
      borderWidth: 1.5,
      borderDash: [6, 6],
      tension: 0,
      spanGaps: true,
      fill: false,
    });
  }

  const data = {
    labels: evolution.map(({ label }) => label),
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        labels: { color: '#929292', boxWidth: 12, boxHeight: 12 },
      },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length === 0) return '';
            return evolution[items[0].dataIndex]?.fullLabel ?? '';
          },
          label: (context) => `${context.dataset.label} : ${formatVACAeroDashboardDuration(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        ...getCommonScale(),
        ticks: { color: '#777777', autoSkip: true, maxRotation: 0 },
      },
      y: {
        ...getCommonScale(),
        beginAtZero: false,
        ticks: {
          color: '#777777',
          callback: (value) => formatVACAeroDashboardDuration(Number(value)),
        },
      },
    },
  };

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} height="h-[340px]">
      <div className="mb-4 shrink-0 text-sm font-semibold uppercase text-perception-gray-400">
        Évolution du temps de cycle moyen
      </div>

      <div className="min-h-0 flex-1">
        {hasData ? <Line data={data} options={options} /> : <VACAeroDashboardNoData />}
      </div>
    </Panel>
  );
};

export const VACAeroDashboardComplianceEvolutionChart = ({ evolution }) => {
  const complianceValues = evolution.map(({ complianceRate }) => complianceRate);
  const hasData = complianceValues.some(Number.isFinite);
  const minValue = getDynamicScoreMin(complianceValues);

  const data = {
    labels: evolution.map(({ label }) => label),
    datasets: [
      {
        ...getCommonLineDataset(COMPLIANCE_COLOR, COMPLIANCE_BACKGROUND_COLOR),
        label: 'Taux de conformité opératoire',
        data: complianceValues,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => {
            if (items.length === 0) return '';
            return evolution[items[0].dataIndex]?.fullLabel ?? '';
          },
          label: (context) => `Taux de conformité : ${formatVACAeroDashboardPercentage(context.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        ...getCommonScale(),
        ticks: { color: '#777777', autoSkip: true, maxRotation: 0 },
      },
      y: {
        ...getCommonScale(),
        min: minValue,
        max: 100,
        ticks: {
          color: '#777777',
          callback: (value) => `${value} %`,
        },
      },
    },
  };

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} height="h-[340px]">
      <div className="mb-4 shrink-0 text-sm font-semibold uppercase text-perception-gray-400">
        Évolution du taux de conformité opératoire
      </div>

      <div className="min-h-0 flex-1">
        {hasData ? <Line data={data} options={options} /> : <VACAeroDashboardNoData />}
      </div>
    </Panel>
  );
};

export const VACAeroDashboardAlertDistribution = ({ distribution }) => {
  const maxCount = distribution.length > 0 ? Math.max(...distribution.map(({ count }) => count)) : 0;

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} height="h-[340px]">
      <div className="mb-4 shrink-0 text-sm font-semibold uppercase text-perception-gray-400">
        Répartition des alertes
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-2">
        {distribution.length === 0 ? (
          <VACAeroDashboardNoData text="Aucune alerte" />
        ) : (
          <div className="flex flex-col gap-4">
            {distribution.map(({ type, name, count, share }) => (
              <div key={type} className="flex flex-col gap-2">
                <div className="flex items-start justify-between gap-4 text-sm">
                  <div className="min-w-0 text-perception-gray-300">{name}</div>

                  <div className="flex shrink-0 gap-2">
                    <span className="text-perception-gray-300">{count}</span>
                    <span className="text-perception-gray-600">{share.toFixed(1).replace('.', ',')}%</span>
                  </div>
                </div>

                <div className="h-2 overflow-hidden rounded-full bg-perception-black-800">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${maxCount > 0 ? count / maxCount * 100 : 0}%`,
                      backgroundColor: type === 'bad_tool_usage' ? BAD_TOOL_USAGE_COLOR : ALERT_COLOR,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
};